import { create } from 'zustand';
import * as THREE from 'three';

/** FP walk → exit walk-out → black fade before ShotAim. */
export type TunnelExitBeat = 'fp' | 'exit' | 'fade';

/** FP eye pose at EXIT_TRIGGER — cinematic must start here (no pull-back). */
export type ExitHandoffCam = {
  x: number;
  y: number;
  z: number;
  lookX: number;
  lookY: number;
  lookZ: number;
  fov: number;
};

interface TunnelExitStore {
  beat: TunnelExitBeat;
  /** Elapsed seconds in the current exit cinematic (reset on exit start). */
  t: number;
  /** 0→1 progress through the exit cinematic. */
  progress: number;
  /** Tunnel décor scroll (negative = walls/rails appear to slide back). */
  envScroll: number;
  /** Captured FP camera at the moment walk ends. */
  handoffCam: ExitHandoffCam | null;
  /** World Z where the hero spawns — same as FP stop (no pull-back). */
  handoffHeroZ: number | null;
  setBeat: (beat: TunnelExitBeat) => void;
  setT: (t: number) => void;
  setProgress: (progress: number) => void;
  setEnvScroll: (envScroll: number) => void;
  setHandoffCam: (cam: ExitHandoffCam | null) => void;
  setHandoffHeroZ: (z: number | null) => void;
  reset: () => void;
}

export const useTunnelExitStore = create<TunnelExitStore>((set) => ({
  beat: 'fp',
  t: 0,
  progress: 0,
  envScroll: 0,
  handoffCam: null,
  handoffHeroZ: null,
  setBeat: (beat) => set({ beat }),
  setT: (t) => set({ t }),
  setProgress: (progress) => set({ progress }),
  setEnvScroll: (envScroll) => set({ envScroll }),
  setHandoffCam: (handoffCam) => set({ handoffCam }),
  setHandoffHeroZ: (handoffHeroZ) => set({ handoffHeroZ }),
  reset: () =>
    set({
      beat: 'fp',
      t: 0,
      progress: 0,
      envScroll: 0,
      handoffCam: null,
      handoffHeroZ: null,
    }),
}));

/** Build handoff from the live FP camera (same distance, look continues). */
export function captureExitHandoff(camera: THREE.Camera): ExitHandoffCam {
  const fwd = new THREE.Vector3();
  camera.getWorldDirection(fwd);
  const look = camera.position.clone().addScaledVector(fwd, 8);
  const fov =
    camera instanceof THREE.PerspectiveCamera ? camera.fov : 54;
  return {
    x: camera.position.x,
    y: camera.position.y,
    z: camera.position.z,
    lookX: look.x,
    lookY: look.y,
    lookZ: look.z,
    fov,
  };
}

if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__tunnelExit =
    useTunnelExitStore;
}
